import axios from "axios";
import FileDownload from "js-file-download";
import { isEmpty, get } from "lodash";
import { toastr } from "react-redux-toastr";
import { log, error } from "../../utils/logger";
import {
  DATASET_ERROR,
  DATA_LOADING,
  DATASET_UPLOAD_PROGRESS,
  DATASET_DOWNLOADING,
  SET_DATA_SUMMARY,
  SET_DATASET,
  SET_DATASETS,
  CREATE_DATASET,
  RESET_DATASETS,
  UPLOAD_DATASET_FILE,
  REMOVE_DATASET_FILE,
  APPLY_MAPPED_COLUMNS,
  DELETE_DATASET,
  SET_FILTERS,
} from "../constants";

function formatError(error) {
  return {
    response: {
      data: { error },
    },
  };
}

const dataLoading = (payload) => (dispatch) => {
  dispatch({ type: DATA_LOADING, payload });
};
const datasetDownloading = (payload) => (dispatch) => {
  dispatch({ type: DATASET_DOWNLOADING, payload });
};

// ================================================================================================
// Data Operations ================================================================================
// ================================================================================================

export const getDataSummary = () => (dispatch) => {
  log(`Getting data summary`);

  dispatch(dataLoading("summary"));

  return axios
    .get(`dataset/summary`)
    .then((res) => {
      const summary = get(res, "data.summary");
      if (isEmpty(summary))
        return dispatch({
          type: DATASET_ERROR,
          payload: { summary: `No data summary found` },
        });

      dispatch({ type: SET_DATA_SUMMARY, payload: summary });

      return summary;
    })
    .catch((err) => {
      error("Error getting data summary", err);
      dispatch({ type: DATASET_ERROR, payload: { summary: err } });
    });
};

// ================================================================================================
// Dataset Operations =============================================================================
// ================================================================================================

let datasetCache, pendingDatasetCall;
export const getDataset = (dataset_id, params) => (dispatch) => {
  if (pendingDatasetCall) {
    error("Blocked dataset refresh: pending call in progress", pendingDatasetCall);
    // current call in process
    return datasetCache;
  }
  pendingDatasetCall = true;

  log(`Getting dataset[${dataset_id}]`);

  dispatch(dataLoading("dataset"));

  datasetCache = axios
    .get(`dataset/${dataset_id}`, { params })
    .then((res) => {
      const dataset = get(res, "data.dataset");
      if (isEmpty(dataset))
        return dispatch({
          type: DATASET_ERROR,
          payload: { dataset: `Dataset [${dataset_id}}] not found` },
        });

      dispatch({ type: SET_DATASET, payload: dataset });

      // Get mapped columns for dataset automatically for non-error datasets
      if (dataset.status_int > 0) {
        dispatch(getMappedColumnsFromDataset(dataset_id));
      }
    })
    .catch((err) => {
      const message = get(err, "response.data.error");
      error("Error getting dataset", message);
      dispatch({ type: DATASET_ERROR, payload: { dataset: message } });
    })
    .finally(() => {
      pendingDatasetCall = false;
    });

  return datasetCache;
};

// Get array of Datasets
let datasetsCache;
export const getDatasets =
  (force_refresh, include = "active") =>
  (dispatch, getState) => {
    const { list } = getState().datasets;

    if (!datasetsCache || !list.length || force_refresh) {
      log("Updating datasets array");

      dispatch(dataLoading("datasets"));

      datasetsCache = axios
        .get(`dataset?include=${include}`)
        .then((res) => {
          const datasets = get(res, "data.datasets");
          if (isEmpty(datasets))
            return dispatch({ type: DATASET_ERROR, payload: { datasets: "No datasets found" } });

          dispatch({ type: SET_DATASETS, payload: datasets });
        })
        .catch((err) => {
          error("Error getting datasets", err);
          dispatch({ type: DATASET_ERROR, payload: { datasets: err } });
        });
    } else {
      log("Using cached datasets");
    }

    return datasetsCache;
  };

// Create new Dataset
export const createDataset = (dataset) => (dispatch) => {
  log("Creating dataset", dataset);

  return axios
    .put(`dataset`, dataset)
    .then((res) => {
      const new_dataset = get(res, "data.dataset");
      if (isEmpty(new_dataset)) {
        throw formatError({ dataset: "Unknown error creating dataset" });
      }

      toastr.success("Success", `New "${dataset.name}" dataset created`);
      dispatch(getDataset(new_dataset.dataset_id_bint));
      dispatch(getDatasets(true));
      dispatch({ type: CREATE_DATASET }); // Clear old dataset values (files array, errors, loading)

      return res.data;
    })
    .catch((err) => {
      const custommsg = get(err, "response.data.error") || "";
      if (/No Data is available for the selected filters/gi.test(custommsg))
        toastr.error("Oops", custommsg);
      else toastr.error("Oops", "There was an issue creating your dataset");
      error("Error creating dataset", err.response ? err.response.data : err.message);
      dispatch({ type: DATASET_ERROR, payload: { dataset: get(err, "response.data.error") } });

      const error_dataset = get(err, "response.data.dataset");
      if (error_dataset) {
        return { ...error_dataset, error: true };
      }
    });
};

export const dipDataset = (dataset) => (dispatch) => {
  log(`Dipping dataset[${dataset.dataset_id_bint}]`);

  toastr.confirm(
    `This will screen your dataset for any mobile phones with the "voice" device type and automatically update them to "sms" instead.`,
    {
      okText: "Run Screen",
      cancelText: "Cancel",
      onOk: () => {
        axios
          .post(`dataset/dip/${dataset.dataset_id_bint}`)
          .then(() => {
            toastr.success(
              `Dipping Dataset "${dataset.title_vch}"`,
              `Depending on your list size, this may take a few minutes. Please check back later`
            );
            dispatch(getDatasets(true)); // Refresh dataset list
          })
          .catch((err) => {
            toastr.error("Oops", "There was an issue dipping your dataset");
            error("Error dipping dataset", err.response ? err.response.data : err.message);
            dispatch({
              type: DATASET_ERROR,
              payload: { dip: get(err, "response.data.error") },
            });
          });
      },
    }
  );
};

export const deleteDataset = (dataset) => (dispatch) => {
  log("Deleting dataset", dataset);

  toastr.confirm(`Are you sure you want to delete dataset "${dataset.title_vch}"?`, {
    okText: "Delete",
    cancelText: "Cancel",
    onOk: () => {
      axios
        .delete(`dataset/${dataset.dataset_id_bint}`)
        .then((res) => {
          toastr.success("Success", `Dataset "${dataset.title_vch}" deleted`);
          dispatch({ type: DELETE_DATASET }); // Notate deletion in state log
          dispatch(getDatasets(true)); // Refresh dataset list
        })
        .catch((err) => {
          toastr.error("Oops", "There was an issue deleting your dataset");
          error("Error deleting dataset", err.response ? err.response.data : err.message);
          dispatch({
            type: DATASET_ERROR,
            payload: { delete: get(err, "response.data.error") },
          });
        });
    },
  });
};

export const getMappedColumnsFromDataset = (dataset_id) => (dispatch) => {
  log("Get mapped columns for dataset", dataset_id);

  return axios
    .get(`dataset/${dataset_id}/mapped_columns`)
    .then((res) => {
      const valid_columns = get(res, "data.valid_columns");
      if (isEmpty(valid_columns)) {
        toastr.error("Sorry", "There was an error getting the mapped columns for this dataset");
        throw formatError({ mapped_columns: "Unknown error getting mapped columns" });
      }

      dispatch({ type: APPLY_MAPPED_COLUMNS, payload: valid_columns });
    })
    .catch((err) => {
      toastr.error("Sorry", "There was an error getting the mapped columns for this dataset");
      error(
        "Error getting mapped columns for dataset",
        err.response ? err.response.data : err.message
      );
      dispatch({
        type: DATASET_ERROR,
        payload: { mapped_columns: get(err, "response.data.error") },
      });
    });
};

export const downloadDataset =
  (dataset_id, file_name = "dataset.csv") =>
  (dispatch) => {
    log("Downloading dataset", dataset_id, "as", file_name);
    dispatch(datasetDownloading(true));
    axios
      .get(`/dataset/download/${dataset_id}`)
      .then((res) => {
        FileDownload(res.data, file_name);
      })
      .catch((err) => {
        const message = get(err, "err.response.data.error") || "Unauthorized";
        toastr.error(message || "Sorry, there was an error downloading your dataset");
      })
      .finally(() => {
        dispatch(datasetDownloading(false));
      });
  };

// ================================================================================================
// Filter Operations ==============================================================================
// ================================================================================================

let filtersCache;
export const getFilters = (force_refresh) => (dispatch, getState) => {
  const { filters } = getState().datasets;

  if (!filtersCache || !filters.length || force_refresh) {
    log("Updating dataset filters array");

    filtersCache = axios
      .get(`dataset/filters`)
      .then((res) => {
        const filters = get(res, "data.filters");
        if (isEmpty(filters)) {
          return dispatch({ type: DATASET_ERROR, payload: { filters: "No filters found" } });
        }

        dispatch({ type: SET_FILTERS, payload: filters });
      })
      .catch((err) => {
        error("Error getting dataset filters", err.response ? err.response.data : err.message);
        dispatch({ type: DATASET_ERROR, payload: { filters: get(err, "response.data.error") } });
      });
  }
  return filtersCache;
};

/**
 * Get the available options for a specific filter from the data
 * @param {String} filter Ex: "contact_vch"
 */
export const getDataFilter = (filter) => {
  return axios.get(`dataset/filter/${filter}`);
};

// ================================================================================================
// File Operations ================================================================================
// ================================================================================================

// Load files from session state
export const loadTempFiles = (files) => (dispatch) => {
  dispatch({ type: UPLOAD_DATASET_FILE, payload: files });
};

export const showFileUploadProgressIcon = (uploadFlag) => (dispatch) => {
  dispatch({ type: DATASET_UPLOAD_PROGRESS, payload: uploadFlag });
};

// Upload temp files to use as dataset sources
export const uploadTempData = (files) => (dispatch) => {
  if (isEmpty(files)) {
    return error("No Files sent for upload");
  }

  log(`Uploading ${files.length} files`);

  const data = new FormData();
  files.forEach((file) => data.append("data", file));

  // '/files' is your node.js route that triggers our middleware
  dispatch(showFileUploadProgressIcon(true));
  return axios
    .post("/dataset/upload", data)
    .then((res) => {
      let temp_files = get(res, "data.files");
      if (isEmpty(temp_files)) {
        return dispatch({ type: DATASET_ERROR, payload: { files: "No files uploaded" } });
      }

      // Re-append original file name
      temp_files = temp_files.map((temp_file) => {
        const found = files.find((file) => file.name === temp_file.name);
        temp_file.original_name = found.original_name;
        return temp_file;
      });

      dispatch({ type: UPLOAD_DATASET_FILE, payload: temp_files });

      return temp_files;
    })
    .catch((err) => {
      toastr.error("Oops", "There was an issue uploading your file(s)");
      error("Error uploading dataset", err.response ? err.response.data : err.message);
      dispatch({ type: DATASET_ERROR, payload: { files: get(err, "response.data.error") } });
    })
    .finally(() => {
      dispatch(showFileUploadProgressIcon(false));
    });
};

export const removeTempData = (files, options) => (dispatch) => {
  if (isEmpty(files)) return error("No Files to remove");

  return axios
    .delete(`dataset/upload`, { data: { files } })
    .then((res) => {
      if (!options || !options.suppressConfirmation) {
        toastr.success("Success", get(res, "data.message"));
      }

      dispatch({ type: REMOVE_DATASET_FILE, payload: get(res, "data.removed") });

      return res.data;
    })
    .catch((err) => {
      toastr.error("Oops", "There was an issue removing your file");
      error("Error removing temp file", err.response ? err.response.data : err.message);
      dispatch({ type: DATASET_ERROR, payload: { files: get(err, "response.data.error") } });
    });
};

//reset landing reducer state
export const resetState = () => ({ type: RESET_DATASETS });
